'use client';

import { useCallback, useEffect, useLayoutEffect, useRef, type RefObject } from 'react';
import { isTextEntry, onPageScrollbar, siteHeaderAutoHide } from '@/lib/site-header-autohide';

const PASSIVE_CAPTURE = { passive: true, capture: true } as const;

/**
 * Whether focus arrived the way a keyboard brings it. A mouse click on a
 * header button also focuses it, and pinning the header for that would keep it
 * on screen until the shopper clicked somewhere else. A text field matches
 * however it was focused, so the header's search box pins it while in use.
 */
function focusVisible(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  try {
    return target.matches(':focus-visible');
  } catch {
    // A browser that cannot parse the selector: treat any focus as visible.
    return true;
  }
}

/**
 * Binds the header to src/lib/site-header-autohide.ts while `enabled` (the
 * marketplace, via siteHeaderAutoHides). The controller decides how far the
 * header is hidden; this writes it to the header's sticky `top` and feeds the
 * controller the page's scrolling and the shopper's inputs.
 *
 * `menuOpen` pins the header fully in (its menu or notifications are open),
 * and so does focus inside it, so a keyboard user tabbing through the nav
 * never has it slide away from under the focus ring.
 *
 * A LAYOUT effect, so the cleanup runs in the same commit as a navigation: a
 * product page gives the header `relative` instead of `sticky`, and a `top`
 * left over from the marketplace would shift it up for a frame.
 */
export function useSiteHeaderAutoHide(
  headerRef: RefObject<HTMLElement | null>,
  enabled: boolean,
  menuOpen: boolean,
): void {
  const menuOpenRef = useRef(menuOpen);
  const focusPinRef = useRef(false);
  const applyPin = useCallback((instant = false) => {
    siteHeaderAutoHide.setPinned(menuOpenRef.current || focusPinRef.current, { instant });
  }, []);

  useLayoutEffect(() => {
    const header = headerRef.current;
    if (!enabled || !header) return;
    const root = document.documentElement;
    const stopWriting = siteHeaderAutoHide.subscribe((hidden) => {
      header.style.top = hidden > 0 ? `-${hidden}px` : '';
    });
    siteHeaderAutoHide.attach({
      // innerHeight rather than the root's clientHeight: on an iPhone it
      // follows the toolbar as it collapses, so the furthest scroll stays
      // exact and the rubber-band past the end is told apart from scrolling.
      scroll: () => ({ y: window.scrollY, maxY: root.scrollHeight - window.innerHeight }),
      height: () => header.offsetHeight,
    });

    const onScroll = () => {
      // A focused control that is removed or made inert can leave without a
      // focusout in some browsers; the pin it set must not outlive it.
      if (focusPinRef.current && !header.contains(document.activeElement)) {
        focusPinRef.current = false;
        applyPin();
      }
      siteHeaderAutoHide.scrolled();
    };
    const onResize = () => siteHeaderAutoHide.resized();
    const onWheel = () => siteHeaderAutoHide.input('wheel');
    // The last finger off, not the first: a second finger still down is still
    // a drag. The same touchend can arrive twice (see onTouchStart), once.
    let lastTouchEnd: Event | null = null;
    const onTouchEnd = (e: TouchEvent) => {
      if (e === lastTouchEnd) return;
      lastTouchEnd = e;
      if (e.touches.length === 0) siteHeaderAutoHide.input('touchend');
    };
    const onTouchStart = (e: TouchEvent) => {
      siteHeaderAutoHide.input('touchstart');
      // A touch's later events all go to the element it began on, and once
      // that element leaves the page (a card re-rendered under the finger)
      // they stop reaching window. Listen on the element too, or the
      // controller would believe the finger never lifted.
      const target = e.target;
      if (!target) return;
      const end = (ev: Event) => {
        if ((ev as TouchEvent).touches.length > 0) return;
        target.removeEventListener('touchend', end);
        target.removeEventListener('touchcancel', end);
        onTouchEnd(ev as TouchEvent);
      };
      target.addEventListener('touchend', end, { passive: true });
      target.addEventListener('touchcancel', end, { passive: true });
    };
    // Bubble phase, so a key a component has already handled (a menu's arrow
    // keys) and never scrolls the page is left out by defaultPrevented.
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        // Shift+Tab from the page into a hidden header: the browser centres
        // the control it focuses using where it sat while hidden, before the
        // focus pin can bring the header in, and the page jumped hundreds of
        // pixels up. The pin shows the header; this puts the page back where
        // the key was pressed, in the next frame, before the jump is painted.
        const y = window.scrollY;
        requestAnimationFrame(() => {
          if (header.contains(document.activeElement) && window.scrollY !== y) {
            window.scrollTo({ top: y, behavior: 'instant' });
          }
        });
      }
      if (!e.defaultPrevented && !isTextEntry(e.target)) siteHeaderAutoHide.input('key');
    };
    const onMouseDown = (e: MouseEvent) => {
      siteHeaderAutoHide.input(e.button === 0 && onPageScrollbar(e, root) ? 'hold' : 'release');
    };
    const onMouseUp = () => siteHeaderAutoHide.input('release');
    const onFocusIn = (e: FocusEvent) => {
      focusPinRef.current = focusVisible(e.target);
      // In at once rather than slid: the browser scrolls the newly focused
      // control into view straight after this event, and a header still off
      // screen would send the page to the top (Shift+Tab from the grid).
      applyPin(true);
    };
    const onFocusOut = (e: FocusEvent) => {
      if (e.relatedTarget instanceof Node && header.contains(e.relatedTarget)) return;
      focusPinRef.current = false;
      applyPin();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('wheel', onWheel, PASSIVE_CAPTURE);
    window.addEventListener('touchstart', onTouchStart, PASSIVE_CAPTURE);
    window.addEventListener('touchend', onTouchEnd, PASSIVE_CAPTURE);
    window.addEventListener('touchcancel', onTouchEnd, PASSIVE_CAPTURE);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('mousedown', onMouseDown, PASSIVE_CAPTURE);
    window.addEventListener('mouseup', onMouseUp, PASSIVE_CAPTURE);
    header.addEventListener('focusin', onFocusIn);
    header.addEventListener('focusout', onFocusOut);
    focusPinRef.current = header.contains(document.activeElement) && focusVisible(document.activeElement);
    applyPin();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('wheel', onWheel, PASSIVE_CAPTURE);
      window.removeEventListener('touchstart', onTouchStart, PASSIVE_CAPTURE);
      window.removeEventListener('touchend', onTouchEnd, PASSIVE_CAPTURE);
      window.removeEventListener('touchcancel', onTouchEnd, PASSIVE_CAPTURE);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('mousedown', onMouseDown, PASSIVE_CAPTURE);
      window.removeEventListener('mouseup', onMouseUp, PASSIVE_CAPTURE);
      header.removeEventListener('focusin', onFocusIn);
      header.removeEventListener('focusout', onFocusOut);
      focusPinRef.current = false;
      siteHeaderAutoHide.setPinned(false);
      siteHeaderAutoHide.detach();
      stopWriting();
      header.style.top = '';
    };
  }, [enabled, headerRef, applyPin]);

  useEffect(() => {
    menuOpenRef.current = menuOpen;
    applyPin();
  }, [menuOpen, applyPin]);
}

/**
 * A callback ref for a bar that sticks under the site header. The bar gets the
 * header's hidden pixels as `--site-header-hidden`, for its sticky `top` to
 * subtract, so it moves up into the room the header leaves and back down as
 * the header returns, in the same frame. Off the marketplace the value stays
 * 0px. A callback ref rather than an effect, so a bar that mounts late (after
 * the marketplace's loading state) still picks the value up before it paints.
 */
export function useRideUnderSiteHeader(): (el: HTMLElement | null) => void {
  const stopRef = useRef<(() => void) | null>(null);
  return useCallback((el: HTMLElement | null) => {
    stopRef.current?.();
    stopRef.current = null;
    if (!el) return;
    const apply = (hidden: number) => el.style.setProperty('--site-header-hidden', `${hidden}px`);
    apply(siteHeaderAutoHide.getHidden());
    stopRef.current = siteHeaderAutoHide.subscribe(apply);
  }, []);
}
